import { initializeApp, type FirebaseApp } from 'firebase/app'
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
  type Messaging,
} from 'firebase/messaging'
import { notificationService } from '@/api/services/notificationService'
import { useNotificationStore } from '@/store/useNotificationStore'
import { toast } from '@/store/useToastStore'

const firebaseConfig = {
  apiKey: 'AIzaSyD5mNFJ9qCIE4k1R4sKcFKySsI0ExhUbiU',
  authDomain: 'drivys.firebaseapp.com',
  projectId: 'drivys',
  storageBucket: 'drivys.firebasestorage.app',
  messagingSenderId: '273250833533',
  appId: '1:273250833533:web:2c1ef5d85811c6b5af4d14',
  measurementId: 'G-0XFDTXR2CR',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const VAPID_KEY: string = (import.meta as any).env?.VITE_APP_PUSH_NOTIFI_KEY ?? ''

let app: FirebaseApp | null = null
let messaging: Messaging | null = null
let foregroundBound = false

// Lazily initialise Firebase Messaging, guarding for browsers/contexts that
// don't support the FCM APIs (returns null there instead of throwing).
async function getMessagingInstance(): Promise<Messaging | null> {
  if (messaging) return messaging
  try {
    if (!(await isSupported())) {
      console.warn(
        '[firebase] FCM not supported here. Most common cause: the page is not a secure context ' +
          '(must be https:// or http://localhost — plain http on a LAN IP will not work). isSecureContext=',
        typeof window !== 'undefined' ? window.isSecureContext : 'n/a'
      )
      return null
    }
    app = app ?? initializeApp(firebaseConfig)
    messaging = getMessaging(app)
    return messaging
  } catch (err) {
    console.error('[firebase] messaging init failed', err)
    return null
  }
}

// Ensure the FCM service worker is registered and return its registration, so we
// can hand it to getToken explicitly (avoids a race with main.tsx's load-time
// registration and makes failures obvious).
async function getSwRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!('serviceWorker' in navigator)) return undefined
  try {
    return (
      (await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')) ??
      (await navigator.serviceWorker.register('/firebase-messaging-sw.js'))
    )
  } catch (err) {
    console.error('[firebase] service worker registration failed', err)
    return undefined
  }
}

/**
 * Request the browser notification permission, then fetch and sync the FCM
 * token with the backend. Safe to call multiple times. Returns the token, or
 * null when unsupported / not granted / on error.
 */
export async function requestNotificationPermission(): Promise<string | null> {
  if (typeof window === 'undefined' || !('Notification' in window)) return null

  const m = await getMessagingInstance()
  if (!m) return null

  if (!VAPID_KEY) {
    console.error(
      '[firebase] VITE_APP_PUSH_NOTIFI_KEY is empty. In dev, restart `npm run dev` after editing .env.'
    )
    return null
  }

  try {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      console.info('[firebase] notification permission not granted:', permission)
      return null
    }

    const serviceWorkerRegistration = await getSwRegistration()
    const token = await getToken(m, { vapidKey: VAPID_KEY, serviceWorkerRegistration })
    if (!token) {
      console.warn('[firebase] no FCM token returned')
      return null
    }
    console.log('[firebase] FCM token:', token)

    try {
      await notificationService.updateFcmToken(token)
      console.log('[firebase] FCM token synced with backend')
    } catch (err) {
      console.error('[firebase] failed to sync FCM token with backend', err)
    }

    listenForegroundMessages()
    return token
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((err as any)?.code === 'messaging/token-unsubscribe-failed') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      console.warn('[firebase] token unsubscribe failed', (err as any)?.message)
    } else {
      console.error('[firebase] permission/token error', err)
    }
    return null
  }
}

/**
 * Bind the foreground message handler (messages received while the tab is
 * focused). Pushes into the notification store and surfaces a toast. Idempotent.
 */
export function listenForegroundMessages(): void {
  if (foregroundBound) return
  void getMessagingInstance().then((m) => {
    if (!m || foregroundBound) return
    foregroundBound = true
    onMessage(m, (payload) => {
      const title = payload?.notification?.title ?? ''
      const body = payload?.notification?.body ?? ''
      const image = payload?.notification?.image

      useNotificationStore.getState().add({ title, body, image })
      if (title) toast.info(title)

      if (title && 'Notification' in window && Notification.permission === 'granted') {
        try {
          new Notification(title, { body, icon: image })
        } catch {
          /* ignore — some browsers only allow SW-shown notifications */
        }
      }
    })
  })
}
